import { v4 } from 'uuid';
import { assertUnreachable } from './utils';
import { VersionAction } from '../../types/lib/survey';

/*
 * Operation-based CRDT with Last-Write-Wins (LWW) conflict resolution.
 * 
 * Uses Lamport timestamps (prev_gen) for causal ordering and LWW semantics for concurrent edits.
 * Arrays use fractional indexing (SortKey) to enable conflict-free insertions.
 * 
 * Trade-offs:
 * - ✅ Simple and performant
 * - ✅ Deterministic convergence via Lamport timestamp + operation ID tiebreaker
 * - ❌ Concurrent edits to the same primitive value result in data loss (one edit wins, other is discarded)
 * 
 * Structure:
 * - TrackedPrimitive: LWW-Register (strings, numbers, booleans)
 * - TrackedRecord: LWW-Map (object properties)
 * - TrackedArray: RGA-like with fractional indexing (ordered lists)
 * 
 * Contains support for undo/redo, but not yet exposed/utilized due to tricky UX questions.
 */

/**
 * Compares two operations for Last-Write-Wins ordering.
 * Returns true if `a` should win over `b`.
 * 
 * Uses Lamport timestamp (prev_gen) as primary sort key, with operation ID
 * as deterministic tiebreaker for concurrent operations.
 */
export function operationWins(a: Move | Patch, b: Move | Patch): boolean {
  if (a.prev_gen > b.prev_gen) return true;
  if (a.prev_gen < b.prev_gen) return false;
  return a.id > b.id;
}

/**
 * Returns the winning operation from an array of operations using LWW conflict resolution.
 * 
 * @param operations - Array of Move or Patch operations to compare
 * @returns The operation that wins according to LWW rules, or undefined if array is empty
 */
export function getWinningOperation<T extends Move | Patch>(operations: T[]): T | undefined {
  if (operations.length === 0) return undefined;
  return operations.reduce((winner, current) => operationWins(current, winner) ? current : winner);
}

export type Author = {
  uid?: string,
  name?: string,
  tabId?: string,
  tags?: string[]
};

export type Patch = {
  op: 'patch',
  /** unique identifier for the patch operation, used to deduplicate patches */
  id: string,
  value: string | number | boolean | null | undefined,
  prev_gen: number,
  author?: Author
}

export type Location = {
  kind: 'array',
  id: string,
  sortKey: SortKey 
} | {
  kind: 'object',
  id: string,
  prop: string
}

/**
 * Used for adding/removing/moving things in arrays and objects.
 * Removal looks like a move to loc=undefined.
 */
export type Move = {
  op: 'move',
  /** unique identifier for the move operation, used to deduplicate moves */
  id: string,
  loc?: Location,
  prev_gen: number,
  author?: Author
}

export type ActiveEditor = {
  uid: string,
  name: string,
  tabId: string,
  lastEditTime: number
}

export type ObjectState = {
  kind: 'array' | 'record'
  locations: Move[]
}

export type PrimitiveState = {
  kind: 'primitive'
  locations: Move[],
  versions: Patch[]
}

export type VersionSource =
  | 'admin_panel'
  | 'content_template'
  | 'copy_editor'
  | 'distro'
  | 'meeting'
  | 'provision'
  | 'unknown';

/**
 * A single change operation tuple.
 * Contains the object ID, its kind, and the operation to apply.
 */
export type Change = [id: string, kind: string, op: Move | Patch];

/**
 * id: the {@link TrackedObject} ID of the object that the operation effects
 * kind: the type of TrackedObject ('array' | 'record' | 'primitive')
 * op: the operation to apply to the TrackedObject (see {@link Move}, {@link Patch})
 * 
 * Could represent:
 * - a "delta" changeset, a subset of changes to apply to a state
 * - a "full" changeset, compressed set of changes which can reconstruct the full survey at a point in time
 * - a "historical" changeset, an uncompressed history of all changes to all objects ever (in practice we don't really store or send this)
 */
export type ChangeSet = Change[];

export type UserActivity = {
  lastEditedObject: string,
  uid: string,
  name: string,
  tabId: string,
  source?: VersionSource,
}

export type State = {
  /** The current author/user editing this document */
  currentAuthor?: Author,
  
  /** Map from userID/tabId to the user Activity for other collab editors */
  userToActivity?: Record<string, UserActivity>,
  
  /** Map from objectID to the users currently editing that object */
  objectToActivity?: Record<string, UserActivity[]>,
  
  /** Current user's activity on the document - used to broadcast cursor/selection to other collaborators */
  myActivity?: UserActivity,
  
  /** Map of all CRDT object states by ID, storing the operation history (locations and patches) for each object */
  objects: Record<string,  ObjectState | PrimitiveState>
  
  /** Map of materialized TrackedObjects by ID - the reconstructed runtime representation of objects from their operation history */
  workingSet: Record<string, TrackedObject>
  
  /** Lamport timestamp representing the current generation/version of the state, incremented with each operation */
  currentRevision: number,
    
  /** Reference counter tracking nested change transactions - when > 0, changes are buffered instead of immediately applied */
  changeSetOpen: number,
  
  /** Offset into the undo stack when undoing/redoing operations - tracks how far back in history we are */
  undoOffset?: number,
  
  /** Temporary buffer holding changes during an open transaction (when changeSetOpen > 0), merged when transaction closes */
  bufferedChanges?: ChangeSet,
  
  /** Stack of ChangeSets that can be undone - operations are pushed here when changes are applied */
  undoStack: ChangeSet[],
  
  /** Stack of ChangeSets that can be redone - operations are pushed here when undo is performed */
  redoStack: ChangeSet[],

  /** All changes (local and from other collaborators) since the last publish. Used to power the Changes tab, AI coach context, and as the delta sent to the server on publish. */
  unpublishedChanges?: ChangeSet,
  
  /** Flag indicating whether we are replaying historical changes (e.g., initial load from storage) vs processing new real-time edits */
  replaying?: boolean,

  /** Flag to enable verbose console logging of CRDT operations for debugging */
  debug?: boolean,

  /** Custom logging function used when `debug` is true; defaults to `console.log` if not provided */
  debugLogger?: (...args: unknown[]) => void,

  /** Root-level attribute names that must not have LWW conflicts — throws an error if a conflict is detected on any of these attributes */
  protectedRootAttributes?: string[],
    
  /** Optional callback invoked when a changeset is committed - used for persistence, synchronization, or reacting to mutations */
  onChangeset?: (events: ChangeSet, replaying?: boolean) => void
}

/**
 * Merges two ChangeSets by applying Last-Write-Wins conflict resolution.
 * 
 * When multiple operations exist for the same object, this function selects the operation
 * with the highest Lamport timestamp (`prev_gen`). When timestamps are equal (concurrent
 * edits), uses operation ID as a deterministic tiebreaker to ensure all clients converge
 * to the same state regardless of operation processing order.
 * 
 * @param left - First ChangeSet (typically local changes)
 * @param right - Second ChangeSet (typically remote changes)
 * @returns A deduplicated ChangeSet containing the winning operations
 */
export function reduceEvents(left: ChangeSet, right: ChangeSet): ChangeSet {
  const moves: Record<string, [string, string, Move]> = {};
  const patches: Record<string, [string, string, Patch]> = {};
  const root: ChangeSet = [];

  for (const [id, kind, op] of [...left, ...right]) {
    if (id === 'root') {
      root.push([id, kind, op]);
    } else if (op.op === 'move' && (!moves[id] || operationWins(op, moves[id][2]))) {
      moves[id] = [id, kind, op];
    } else if (op.op === 'patch' && (!patches[id] || operationWins(op, patches[id][2]))) {
      patches[id] = [id, kind, op];
    }
  }

  return [...root, ...Object.values(moves), ...Object.values(patches)];
}

/**
 * Derives a branch's unpublished changes from ops files instead of the unpublished-events
 * table: ops present in the branch ops file but not in the parent ops file, plus pending
 * events not yet compacted into the branch file (deduped by op id).
 *
 * Because compaction squashes history to LWW winners, this yields the winning op per
 * changed object rather than every intermediate event — equivalent for state and for
 * per-object change rendering.
 */
export function computeUnpublishedFromOps(parentOps: ChangeSet, branchOps: ChangeSet, pendingEvents: ChangeSet): ChangeSet {
  const parentOpIds = new Set(parentOps.map(e => e[2]?.id).filter(Boolean));
  const branchOpIds = new Set(branchOps.map(e => e[2]?.id).filter(Boolean));
  const branchOnlyOps = branchOps.filter(e => e[2]?.id && !parentOpIds.has(e[2].id));
  const pendingNotCompacted = pendingEvents.filter(e => !e[2]?.id || !branchOpIds.has(e[2].id));
  return [...branchOnlyOps, ...pendingNotCompacted];
}


/**
 * Creates a minimal snapshot of the CRDT state by pruning history and unreachable objects.
 * 
 * This function performs two key optimizations:
 * 1. **Garbage collection**: Removes objects unreachable from the root, freeing memory
 * 2. **History compression**: Reduces each object's operation history to its latest state
 * 
 * The result is a ChangeSet containing only the most recent Move and Patch operations
 * for each reachable object, which can reconstruct the current state without storing
 * the full operation history.
 * 
 * **Algorithm**:
 * - Traverses object graph from root to identify reachable objects
 * - Deletes unreachable objects (those not in the traversal set)
 * - For each remaining object, keeps only the latest Move (location)
 * - For primitives, also keeps only the latest Patch (value)
 * 
 * **Use cases**:
 * - Persisting state to storage (checkpointing)
 * - Sending full state over the network to new collaborators
 * - Reducing memory footprint of long-running sessions
 * - Creating snapshots before major operations
 * 
 * @param state - The CRDT state to compress
 * @param rematerializeState - If true, rematerializes the state from the compressed events. If false, uses the working set. Set to false when you trust that the state has not had direct changes to its internal properties.
 * @returns A minimal ChangeSet that can reconstruct the current state via `reconstructStateFromEvents`
 * 
 * @remarks
 * - Mutates `state.objects` by deleting unreachable entries
 * - Logs pruning statistics to console
 * - Objects without a root are not pruned (entire state is preserved)
 */
export function getCompressedEvents(state: State, rematerializeState: boolean = true): ChangeSet {
  // If there's a root we can safely prune
  if (state.objects['root']) {
    // Find the reachable set of objects
    const obj = rematerializeState ? ReconstructState(state)[0] : state.workingSet['root'];

    const reachable = new Set<string>();
    const toVisit = [obj];
    while (toVisit.length) {
      const n = toVisit.pop()!;
      reachable.add(n.id);

      if (Array.isArray(n.value)) {
        n.value.forEach(v => toVisit.push(v))
      } else if (typeof n.value === 'object' && n.value !== null) {
        Object.values(n.value).forEach(v => toVisit.push(v as TrackedObject));
      }
    }

    // Find the set of objects that have no parents
    const unreachable = new Set<string>();
    for (const id in state.objects) {
      if (!reachable.has(id)) {
        unreachable.add(id);
      }
    }

    // An unreachable object whose location points at an id we have no ops for may be part
    // of a changeset that has only partially arrived (compaction can observe a subset of a
    // batch) — keep its ops so the subtree can reattach when the parent's op shows up.
    // That protection extends to its descendants: anything located under a kept orphan is
    // kept too. Objects located under a known parent were genuinely detached/deleted.
    const keepDecision = new Map<string, boolean>();
    const isPendingOrphan = (id: string): boolean => {
      const memo = keepDecision.get(id);
      if (memo !== undefined) return memo;
      keepDecision.set(id, false); // cycle guard
      const loc = getWinningOperation(state.objects[id].locations)?.loc;
      let keep = false;
      if (loc) {
        if (!state.objects[loc.id]) {
          keep = true;
        } else if (!reachable.has(loc.id)) {
          keep = isPendingOrphan(loc.id);
        }
      }
      keepDecision.set(id, keep);
      return keep;
    };

    // Decide before deleting anything so parent lookups see a consistent snapshot
    const toDelete: string[] = [];
    for (const id of unreachable) {
      const latestLoc = getWinningOperation(state.objects[id].locations);
      // If locations is empty, delete object bc it is hanging in outer space
      if (!latestLoc) {
        toDelete.push(id);
      }
      // If there is a location under a known parent, it's parented by the actual thing that was moved out/deleted so we can safely delete it
      else if (latestLoc.loc && !isPendingOrphan(id)) {
        toDelete.push(id);
      }
    }
    for (const id of toDelete) {
      delete state.objects[id];
    }
    console.log('Pruning unreachable', toDelete.length, 'of', unreachable.size);
  }

  const events: ChangeSet = [];
  for (const id in state.objects) {
    const obj = state.objects[id];
    const latestLoc = getWinningOperation(obj.locations)!;
    events.push([id, obj.kind, latestLoc]);
    if (obj.kind === 'primitive') {
      const latestValue = getWinningOperation(obj.versions)!;
      events.push([id, obj.kind, latestValue]);
    }   
  }

  console.log("Compressed event count", events.length);
  return events;
}

/**
 * Reconstructs a complete CRDT state from a ChangeSet, creating a fresh State and root TrackedObject.
 * 
 * This is the primary entry point for initializing a CRDT from persisted or transmitted events,
 * such as when:
 * - Loading a saved document from storage
 * - Initializing a new collaborator with existing state
 * - Restoring from a snapshot/checkpoint
 * - Replaying operations for debugging or testing
 * 
 * The function creates a new empty State, applies all operations from the ChangeSet,
 * and reconstructs the object graph starting from the root.
 * 
 * @param events - ChangeSet containing all operations to apply (typically from `getCompressedEvents`)
 * @param options.debug - If true, enables verbose logging of CRDT operations for troubleshooting
 * @param options.debugLogger - Custom logging function used when `debug` is true; defaults to `console.log`.
 *   Useful for prepending a program name or routing logs to a custom sink.
 * @param options.protectedRootAttributes - Root-level attribute names that must not have LWW conflicts; throws if any of them conflict
 * @param options.onStateChangeCallback - Optional callback invoked whenever TrackedObjects are mutated
 *   (called via `State.onChangeset()` when `changeSetOpen > 0`). Useful for persistence or network sync.
 * @param options.isInitialLoad - If true, sets `state.replaying = true` to prevent operations from being
 *   added to `unpublishedChanges` (since these events are already published/persisted)
 * @returns Tuple of `[rootTrackedObject, state]` - the reconstructed root and its CRDT state
 * 
 * @see {@link mergeStateFromEvents} for adding events to existing state
 * @see {@link getCompressedEvents} for creating a minimal ChangeSet from state
 */
export function reconstructStateFromEvents(events: ChangeSet, options?: {
  debug?: boolean,
  debugLogger?: (...args: unknown[]) => void,
  onStateChangeCallback?: Parameters<typeof createInitialState>[0],
  isInitialLoad?: boolean,
  protectedRootAttributes?: string[],
}): [TrackedObject, State] {
  const state = createInitialState(options?.onStateChangeCallback);
  state.replaying = options?.isInitialLoad;
  state.debug = options?.debug;
  state.debugLogger = options?.debugLogger;
  state.protectedRootAttributes = options?.protectedRootAttributes;
  return mergeStateFromEvents(state, events);
}

/**
 * Merges a ChangeSet into existing CRDT state, incrementally applying new operations.
 * 
 * Unlike `reconstructStateFromEvents` which creates a fresh State, this function adds
 * operations to an existing State, making it suitable for:
 * - Receiving remote changes during active collaboration
 * - Applying incremental updates from network sync
 * - Merging changes from multiple sources
 * - Adding operations without losing local unpublished changes
 * 
 * **Algorithm**:
 * - For each event, ensures the target object exists in `state.objects`
 * - Resolves LWW conflict inline: each incoming op is compared to the currently stored
 *   winner via `operationWins`. If it wins, it replaces the stored op; otherwise it is
 *   discarded. This keeps `locations` and `versions` arrays bounded at exactly 1 entry
 *   per object (only objects touched by the incoming batch are affected).
 * - Rebuilds the working set via `ReconstructState` to materialize the updated state
 * 
 * LWW conflict resolution selects the operation with the highest `prev_gen` for each
 * object. When `prev_gen` values are equal (concurrent edits), operation ID is used as
 * a deterministic tiebreaker to ensure convergence.
 * 
 * @param state - Existing CRDT state to merge events into (mutated in place)
 * @param events - ChangeSet containing operations to merge
 * @returns Tuple of `[rootTrackedObject, state]` - the updated root and state
 * 
 * @see {@link reconstructStateFromEvents} for creating state from scratch
 * @see {@link ReconstructState} for the working set materialization logic
 */
export function mergeStateFromEvents(state: State, events: ChangeSet): [TrackedObject, State] {
  for (const [id, kind, op] of events) {
    state.objects[id] = state.objects[id] || (
      kind === 'primitive' ?
        {kind: 'primitive', locations: [], versions: []}
        : {kind, locations: []});
    // Root has no ops on it
    if (id !== 'root') {
      if (op.op === 'move') {
        // Keep only the winning move — LWW losers can never become winners, so discard inline
        // rather than accumulating and trimming later. Only objects touched by this batch are affected.
        const existing = getWinningOperation(state.objects[id].locations);
        if (!existing || operationWins(op, existing)) {
          state.objects[id].locations = [op];
        }
      } else if (op.op === 'patch' && kind === 'primitive') {
        const existing = getWinningOperation((state.objects[id] as PrimitiveState).versions);
        if (!existing || operationWins(op, existing)) {
          (state.objects[id] as PrimitiveState).versions = [op];
        }
      }
    }   
  }

  return ReconstructState(state);
}

/** This function does a version of ReconstructState, that returns a collection of disjointed TrackedObjects. */
export function groupObjectsFromDisjointedChanges(events: ChangeSet): Record<string, { object: TrackedObject, events: ChangeSet }> {
  const mockState = {
    objects: {},
    workingSet: {},
    currentRevision: 0,
    changeSetOpen: 0,
    undoStack: [],
    redoStack: [],
  }

  // root is probably undefined here, since we are creating an incomplete state.
  const [root, state] = mergeStateFromEvents(mockState, events);
  const groupedObjects = Object.values(state.workingSet).filter(obj => !obj.parent);
  console.log('groupedObjects after mergeStateFromEvents', groupedObjects.map(o => o.read()));

  // create a map of all the objects that are grouped together.
  // this maps parent id to the TrackedObject.
  const groupedObjectsMap: Record<string, { object: TrackedObject, events: ChangeSet }> = {};
  for (const obj of groupedObjects) {
    groupedObjectsMap[obj.id] = { object: obj, events: [] };
  }

  for (const [id, kind, op] of events) {
    // if the event is one of our grouped object parents, add it to the grouped events.
    if (groupedObjectsMap[id]) {
      groupedObjectsMap[id].events.push([id, kind, op]);
    } else {
      // otherwise, we need to find who its parent is.
      let parent = state.workingSet[id]?.parent;
      let found = false;
      while (!!parent) {
        if (groupedObjectsMap[parent.id]) {
          groupedObjectsMap[parent.id].events.push([id, kind, op]);
          found = true;
          break;
        }
        parent = parent.parent;
      }

      if (!found) {
        console.log('UH OH', id, kind, op);
      }
    }
  }

  // I need to only return objects that do not have a parent - they are the "leader" of their group. 
  return groupedObjectsMap;
}

export function updateUserActivity(state: State, newUserActivityList: UserActivity[]): State {
  for (const newUserActivity of newUserActivityList) {
    const lastActivityFromUser = state.userToActivity?.[newUserActivity.uid + '/' + newUserActivity.tabId];
    if (lastActivityFromUser && lastActivityFromUser.lastEditedObject === newUserActivity.lastEditedObject) {
      return state;
    }
  
    if (!state.userToActivity) {
      state.userToActivity = {};
    }
    if (!state.objectToActivity) {
      state.objectToActivity = {};
    }
    // update user activity map with the new location for the user
    state.userToActivity[newUserActivity.uid + '/' + newUserActivity.tabId] = newUserActivity;
  
    removeLastActivityFromObject(state, lastActivityFromUser);
    state.objectToActivity[newUserActivity.lastEditedObject] = [...(state.objectToActivity[newUserActivity.lastEditedObject] || []), newUserActivity];
  }

  return state;
}

export function removeUserActivityForUser(state: State, uid: string, tabId: string) {
  if (!state.userToActivity) {
    return;
  }

  const key = uid + '/' + tabId;
  const lastActivityFromUser = state.userToActivity[key];

  removeLastActivityFromObject(state, lastActivityFromUser);
  delete state.userToActivity[key];
} 

function removeLastActivityFromObject(state: State, lastActivityFromUser: UserActivity | undefined) {
  if (!state.objectToActivity || !lastActivityFromUser) {
    return;
  }
  
  // remove user from previous location
  if (lastActivityFromUser) {
    state.objectToActivity[lastActivityFromUser.lastEditedObject] = (state.objectToActivity[lastActivityFromUser.lastEditedObject] || [])
      .filter(ua => ua.uid !== lastActivityFromUser.uid || ua.tabId !== lastActivityFromUser.tabId);
  }
}

export function openChangeset(state: State) {
  state.changeSetOpen += 1;
  if (state.changeSetOpen == 1) {
    state.undoStack.push([]);
  }
}

export function closeChangeset(state: State) {
  state.changeSetOpen -= 1;
  if (state.changeSetOpen === 0) {
    if (state.bufferedChanges) state.onChangeset?.(state.bufferedChanges);

    // Empty buffered changes so that the changeset we pass from distro to distro 
    // can't grow super huge.
    state.bufferedChanges = [];
  }
}

/** Appends without `push(...arr)` — spreading huge arrays exceeds the engine argument limit and throws RangeError / stack overflow. */
function appendChangeSet(target: ChangeSet, source: ChangeSet): void {
  for (let i = 0; i < source.length; i++) {
    target.push(source[i]);
  }
}

/**
 * @param event: (optional) - if passed in, this function will be called
 * whenever a changeset is committed (which is anytime a TrackedObject is mutated and
 * `changeSetOpen > 0`), equivalent to `state.onChangeset`.
 */
export function createInitialState(event?: (events: ChangeSet, myActivity?: UserActivity) => void, objects?: State['objects']): State {
  const state: State = {
    objects: objects || {},
    workingSet: {},
    currentRevision: 0,
    changeSetOpen: 0,
    undoStack: [],
    redoStack: [],

    onChangeset: (changeset) => {
      state.currentRevision += 1; 
      
      if (!state.replaying) {
        if (!state.unpublishedChanges) {
          state.unpublishedChanges = [];
        }
        appendChangeSet(state.unpublishedChanges, changeset);
      }

      if (state.changeSetOpen) {
        appendChangeSet(state.undoStack[state.undoStack.length - 1], changeset);
        if (!state.bufferedChanges) {
          state.bufferedChanges = [];
        }
        appendChangeSet(state.bufferedChanges, changeset);
      } else {
        state.undoStack.push(changeset);
        if (event) {
          event(changeset, state.myActivity);
        }
      }
    }
  }
  if (objects) {
    ReconstructState(state);
  }
  return state;
}

export type WorkingSet = Record<string, TrackedObject>;

export type Context = {
  state: State,
  workingSet: WorkingSet,
}

/**
 * Rebuilds the working set of TrackedObjects from raw CRDT state, applying LWW conflict resolution.
 * 
 * This is the core function that materializes the current document state from operation history.
 * It merges operations from local and incoming states, resolves conflicts using Lamport timestamps,
 * and constructs the parent-child relationships that form the object graph.
 * 
 * **Algorithm phases**:
 * 
 * 1. **Merge phase**: Combines `local.objects` with `incoming.objects`
 *    - Merges `locations` arrays (deduplicating by operation id)
 *    - Merges `versions` arrays for primitives (deduplicating by operation id)
 * 
 * 2. **Construct phase**: Creates or updates TrackedObjects in `workingSet`
 *    - For primitives: Selects value from Patch with highest `prev_gen`
 *    - For arrays/records: Creates empty TrackedArray/TrackedRecord if new
 * 
 * 3. **Associate phase**: Builds parent-child relationships
 *    - For each object, finds Move with highest `prev_gen` to determine current location
 *    - Arrays: Assigns `sortKey` and adds child to parent's value array
 *    - Records: Sets child as property on parent object
 *    - Handles removal (location=undefined) and missing parents gracefully
 * 
 * 4. **Finalize phase**: Sorts arrays by sortKey and removes duplicates
 * 
 * **LWW conflict resolution**: When multiple operations exist for the same object,
 * the operation with the highest `prev_gen` (Lamport timestamp) wins. When `prev_gen`
 * values are equal (concurrent edits), uses operation ID as a deterministic tiebreaker
 * to ensure all clients converge to the same state.
 * 
 * @param local - The local CRDT state to rebuild (mutated in place)
 * @param incoming - Optional additional state to merge in (e.g., from remote peer)
 * @returns Tuple of `[rootTrackedObject, state]` - the materialized root and updated state
 * 
 * @remarks
 * - Mutates `local.objects` and `local.workingSet` in place
 * - Handles missing parents gracefully (logs in debug mode but continues)
 * - Used internally by both `mergeStateFromEvents` and `reconstructStateFromEvents`
 */
export function ReconstructState(local: State, incoming?: State): [TrackedObject, State] {
  incoming = incoming || createInitialState();

  // TODO: Track dirty arrays and records
  const dirty = {} as Record<string, true>;

  // Construct
  for (const key of [...Object.keys(incoming.objects), ...Object.keys(local.objects)]) {
    // If we've never seen this before make new state
    if (!local.objects[key] && incoming.objects[key]) {
      local.objects[key] = incoming.objects[key]
      // Otherwise merge the state into the state for this key
    } else if (incoming.objects[key]) {
      const locs = local.objects[key].locations.concat(incoming.objects[key].locations);
      // Remove duplicate locations
      local.objects[key].locations = [...new Map(locs.map(item => [item.id, item])).values()]

      if (local.objects[key].kind === 'primitive') {
        const vals = (local.objects[key] as PrimitiveState).versions
          .concat((incoming.objects[key] as PrimitiveState).versions);
        // Remove duplicate versions/patches
        (local.objects[key] as PrimitiveState).versions = [...new Map(vals.map(item => [item.id, item])).values()]
      }
    }

    const obj = local.objects[key];

    if (obj.kind === 'primitive') {
      const winningPatch = getWinningOperation(obj.versions);
      const value = winningPatch?.value;

      // If this is new, set up the tracked object otherwise just update the value
      if (!local.workingSet[key]) {
        local.workingSet[key] = new TrackedPrimitive(local, key, value);
      } else {
        if (local.workingSet[key].value !== value) {
          local.workingSet[key].value = value;
          dirty[key] = true;
        }
      }
    }
    if (obj.kind === 'array') {
      if (!local.workingSet[key]) {
        local.workingSet[key] = new TrackedArray(local, key, [])
      }
    }
    if (obj.kind === 'record') {
      if (!local.workingSet[key]) {
        local.workingSet[key] = new TrackedRecord(local, key, {});
      }
    }
  }

  // Associate 
  const objProps = {} as Record<string, string[]>;
  for (const key of Object.keys(local.objects)) {
    const obj = local.objects[key];

    const winningMove = getWinningOperation(obj.locations);
    const location = winningMove?.loc;

    if (location) {
      if (location.kind === 'array') {
        local.workingSet[key].sortKey = location.sortKey;

        if (local.workingSet[key].parent?.value) {
          local.workingSet[key].parent!.value = local.workingSet[key].parent!.value
            .filter((v: TrackedObject) => v !== local.workingSet[key]);
        }
        local.workingSet[key].parent = local.workingSet[location.id];

        // If the parent does not exist, it must have been deleted by and earlier event.
        // We do not want to throw an error here.
        if (local.workingSet[location.id]?.value) {
          local.workingSet[location.id].value.push(local.workingSet[key]);
        } else {
          if (local.debug) {
            (local.debugLogger ?? console.log)('COLLAB DEBUG', `This object (${JSON.stringify(local.workingSet[key].read())}, ${key}) does not have a parent. Alleged parent: ${location.id}`, `This was intentionally ignored.`);
          }
        }
      } else if (location.kind === 'object') {
        objProps[location.id + location.prop] ||= [];
        const op = objProps[location.id + location.prop];

        if (local.workingSet[key].parent) {
          for (const k of Object.keys(local.workingSet[key].parent!.value)) {
            if (local.workingSet[key].parent!.value[k] === local.workingSet[key]) {
              delete local.workingSet[key].parent!.value[k];
            }
          }
        }

        // Same as above, we might not have received an event that creates the parent
        // ie, the parent was deleted, and we will not worry about this child node.
        if (local.workingSet[location.id]?.value &&
          (op.length === 0 || op.every(k => key < k))) {
          local.workingSet[location.id].value[location.prop] = local.workingSet[key];
          local.workingSet[key].parent = local.workingSet[location.id];
        }
        if (op.length > 0 && location.id === "root") {
          if (local.protectedRootAttributes?.includes(location.prop)) {
            throw new Error(`COLLAB: root.${location.prop} has a conflict! ${key} < ${op.join(', ')}`);
          }
          if (local.debug) {
            (local.debugLogger ?? console.log)(`COLLAB DEBUG: root.${location.prop} has a conflict! ${key} < ${op.join(', ')}`);
          }
        }
        op.push(key)
      }
    } else {
      // Handle the removal case
      if (local.workingSet[key].parent) {
        const p = local.workingSet[key].parent!;
        local.workingSet[key].parent = undefined;
        if (Array.isArray(p.value)) {
          p.value = p.value.filter((v: TrackedObject) => v !== local.workingSet[key]);
        } else if (typeof p!.value === 'object') {
          for (const k of Object.keys(p.value)) {
            if (p.value[k] === local.workingSet[key]) {
              delete p.value[k];
            }
          }
        } else {
          throw new Error('Invalid parent');
        }
      }
    }
  }

  // Sort and remove duplicates
  for (const key of Object.keys(local.objects)) {
    const obj = local.objects[key];
    if (obj.kind === 'array') {
      local.workingSet[key].value.sort((a: TrackedObject, b: TrackedObject) => 
        SortFunction(a.sortKey!, b.sortKey!));

      local.workingSet[key].value = local.workingSet[key].value.reduce(
        (a: TrackedObject[], b: TrackedObject) => {
          if (a && a.slice(-1)[0]?.id === b.id) {
            return a;
          }
          a.push(b);
          return a;
        }, []);

    }
  }

  // ReconstructState rewires parents/values directly (bypassing the mutators that keep
  // the read() cache fresh), so any cached read() on an existing node may now be stale.
  // Rather than track exactly what moved, blanket-invalidate every node's cache here — a
  // merge/reconcile is an infrequent, already-O(n) operation, so the extra pass is cheap
  // and keeps the caching logic simple. Fine-grained invalidation here can come later.
  for (const key of Object.keys(local.workingSet)) {
    const node = local.workingSet[key];
    if (node && !(node instanceof TrackedPrimitive)) {
      node._readDirty = true;
      node._cachedRead = undefined;
    }
  }

  return [local.workingSet['root'], local]
}

export type TrackedObject = TrackedArray | TrackedPrimitive | TrackedRecord;

const _str = (v: unknown): string => (v && typeof v === 'string') ? v : '';

/**
 * Returns a human-readable label for a survey component TrackedObject by checking
 * common label fields in priority order.
 */
export function getComponentLabel(obj: TrackedObject): string {
  if (!(obj instanceof TrackedRecord)) return '';
  const v = obj.value;
  return _str(v['title']?.value?.['en']?.value)
    || _str(v['title']?.value)
    || _str(v['name']?.value?.['en']?.value)
    || _str(v['name']?.value)
    || _str(v['targetField']?.value)
    || _str(v['target']?.value)
    || _str(v['field']?.value)
    || _str(v['path']?.value);
}

/**
 * Walks up the TrackedObject parent chain collecting { kind, label } for each
 * ancestor that has a survey `kind` value, or (when componentKindsOnly is false)
 * the property name it's stored under in its parent record.
 * Returns segments in root-first order.
 */
export function getTrackedObjectBreadcrumbPath(
  obj: TrackedObject | undefined,
  options?: { componentKindsOnly?: boolean }
): Array<{ kind: string, label: string }> {
  const path: Array<{ kind: string, label: string }> = [];
  let current = obj;
  while (current && current.parent) {
    const node = current;
    const kind = node instanceof TrackedRecord ? _str(node.value['kind']?.value) : '';
    if (kind) {
      path.push({ kind, label: getComponentLabel(node) });
    } else if (!options?.componentKindsOnly) {
      const parent = node.parent;
      if (parent instanceof TrackedRecord) {
        const key = Object.keys(parent.value).find(k => parent.value[k]?.id === node.id);
        if (key) path.push({ kind: key, label: '' });
      }
    }
    current = current.parent;
  }
  return path.reverse();
}

/**
 * Returns the 1-based position of obj within its parent array, or undefined
 * if the parent is not an array or the object isn't found.
 */
export function getPositionInParent(obj: TrackedObject | undefined): number | undefined {
  if (!obj?.parent) return undefined;
  if (obj.parent instanceof TrackedArray) {
    const index = obj.parent.value.findIndex(item => item.id === obj.id);
    return index >= 0 ? index + 1 : undefined;
  }
  return undefined;
}

export type SortKey = number[];
export function SortFunction(a: SortKey, b: SortKey) {
  let depth = 0;
  while (a[depth] !== undefined && a[depth] === b[depth]) {
    depth += 1;
  }
  if (a[depth] !== undefined && b[depth] !== undefined) {
    if (a[depth] === b[depth]) return 0;
    return a[depth] > b[depth] ? 1 : -1;
  } else if (a[depth] !== undefined) {
    return 1;
  } else {
    return -1;
  }
}

function mulberry32(a: number) {
  return function() {
    var t = a += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
}

export function generateUserLocation(author: Author, objID: string, source?: VersionSource) : UserActivity {
  return {
    uid: author.uid!,
    name: author.name || author.uid!,
    tabId: author.tabId || '',
    lastEditedObject: objID,
    source: source || 'distro'
  }
}

// Use a deterministic PRNG in test for reproducibility
// In production/elsewhere we want to ensure that we have as much random diversity
// to that two clients don't resolve the same random number.
const rng = typeof process ? (process.env.NODE_ENV === 'test' ? mulberry32(0) : Math.random) : Math.random;

export function MakeKey(before?: SortKey, after?: SortKey) {
  if (!before && !after) {
    return [Math.round(10000*rng())]
  } else if (!before && after) {
    return [after[0] - Math.round(10000*rng()) - 1]
  } else if (before && !after) {
    return [before[0] + Math.round(10000*rng()) + 1];
  } else if (before && after) {
    let depth = 0;
    let out = [];
    while (before[depth] === after[depth]) {
      out.push(before[depth])
      depth += 1;
    }
    if (before[depth] !== undefined && after[depth] !== undefined) {
      // If before is longer, return before with the last element incremebted
      if (before.length > after.length) {
        while (depth !== before.length - 1) {
          out.push(before[depth])
          depth += 1;
        }
        out.push(before[depth] + Math.round(10000*rng()) + 1)
        // If there's no more room, bump down a level
      } else if (before[depth] + 1 === after[depth]) {
        out.push(before[depth]);
        out.push((before[depth + 1] ? before[depth + 1] : 0) + Math.round(10000*rng() + 1));
        // Split the difference (with some randomness)
      } else {
        let diff = after[depth] - before[depth];
        out.push(before[depth] + Math.round(diff*(0.5 + (rng() - 0.5)*0.3)));
      }
    } else if (before[depth] !== undefined && !after[depth]) {
      out.push(before[depth] + Math.round(10000*rng()) + 1)
    } else if (!before[depth] && after[depth] !== undefined) {
      out.push(after[depth] - Math.round(10000*rng()) - 1)
    }
    const outString = JSON.stringify(out)
    if (JSON.stringify(before) === outString || JSON.stringify(after) === outString) {
      throw new Error('Bad Key Split: ' + JSON.stringify([before, after, out]))
    }
    return out;
  }
  throw Error('Unreachable');
}

/**
 * Invalidates the cached `read()` result for `obj` and walks up the parent chain doing
 * the same, since an ancestor's cached read embeds this subtree's materialized value.
 * 
 * Stops as soon as it hits an ancestor that's already dirty — that ancestor's own
 * ancestors must have been marked dirty already the first time it became dirty.
 * 
 * TrackedPrimitives don't cache `read()` (it's already O(1)), so they're skipped over
 * on the way up rather than treated as a stopping point.
 */
function invalidateReadCache(obj: TrackedObject | undefined): void {
  let current = obj;
  while (current) {
    if (current instanceof TrackedPrimitive) {
      current = current.parent;
      continue;
    }
    if (current._readDirty) return;
    current._readDirty = true;
    current._cachedRead = undefined;
    current = current.parent;
  }
}

/**
 * Drops the cached `read()` output for `obj` and everything below it. Call this the moment
 * a node is detached from the tree: `workingSet` never deletes tombstoned nodes (CRDT
 * tombstones are permanent until pruned), so without this an orphaned subtree pins its materialized
 * `read()` output for the life of the session — exactly the memory this cache exists to
 * save. Recurses because every container in the subtree holds its own cache, not just the
 * detached root. (`invalidateReadCache` walks *up* to refresh ancestors that still embed
 * this subtree; this walks *down* to release the subtree that's leaving.)
 */
function dropReadCacheDeep(obj: TrackedObject | undefined): void {
  if (!obj || obj instanceof TrackedPrimitive) return;
  obj._cachedRead = undefined;
  obj._readDirty = true;
  if (Array.isArray(obj.value)) {
    for (const child of obj.value) dropReadCacheDeep(child);
  } else if (obj.value && typeof obj.value === 'object') {
    for (const k of Object.keys(obj.value)) dropReadCacheDeep(obj.value[k]);
  }
}

// The memoized read() result is a shared reference (see the @returns notes on read()).
// Shallow-freeze it so a caller that mutates what it wrongly assumes is its own copy
// fails loudly at the mutation site instead of silently corrupting every later read()
// — including the survey JSON.stringify'd and written to S3. Materialization already
// allocated this object; one Object.freeze per container is cheap next to a wrong
// published survey. Nested containers are frozen by their own cached read(), so the
// whole tree is frozen without a recursive walk. Callers that need a mutable copy
// pass useCache: false, which skips this path entirely.
function freezeReadResult<T>(value: T): T {
  Object.freeze(value);
  return value;
}

export class TrackedArray {
  id: string
  value: TrackedObject[]
  state: State
  parent?: TrackedObject
  sortKey?: SortKey
  newlyAdded?: boolean
  /** Cache of the last `read()` result. Invalidated (via `_readDirty`) whenever this array's membership changes. */
  _cachedRead?: any[]
  _readDirty: boolean = true
  constructor (state: State, id?: string, value?: TrackedObject[]) {
    this.id = id || 'id' + Math.random();
    this.state = state
    this.value = [];
    if (value && value.length) {
      // Janky heuristic to see if the values have a location set already
      if (state.objects[value[0].id].locations.length == 0) {
        value.map((v, i) => {
          if (state.objects[v.id].locations.length == 0) {
            this.insert(i, v);
          }
        });
      } else {
        this.value = value;
        // These children already carry their own locations, so we deliberately skip
        // insert() — but read()-cache invalidation walks child.parent upward, so each
        // child must still point back at us or a later child mutation would invalidate
        // the wrong array and this one would serve a stale cached read(). (Reachable via
        // ChangeUtils.deepCopyObject, whose copied children retain non-empty locations.)
        for (const child of value) child.parent = this;
      }
    }
    if (!state.objects[this.id]) {
      state.objects[this.id] = {
        kind: 'array',
        locations: []
      }
    }
    state.workingSet[this.id] = this;
  }
  locateUserHere(): void {
    // update myActivity so other distro users can see what object I just added.
    if (this.state.currentAuthor?.uid) {
      this.state.myActivity = generateUserLocation(this.state.currentAuthor, this.id);
    }
  }
  insert(index: number | SortKey, value: TrackedArray | TrackedPrimitive | TrackedRecord, silent?: true): void {
    // TODO: Remove from somewhere else
    const v = (this.state.objects[value.id] as PrimitiveState).locations;
    if (typeof index === 'number') {
      value.sortKey = MakeKey(this.value[index - 1]?.sortKey, this.value[index]?.sortKey);
    } else { 
      value.sortKey = index;
      index = this.value.filter((v) => SortFunction(v.sortKey!, index as SortKey) < 0).length;
    }
    const op: Move = {
      op: 'move',
      id: Math.random().toString(),
      loc: {
        kind: 'array',
        id: this.id,
        sortKey: value.sortKey
      },
      prev_gen: Math.max(0, ...v.map(p => p.prev_gen + 1)),
      author: this.state.currentAuthor
    };
    v.splice(0, v.length, op);
    this.value.splice(index, 0, value);
    value.parent = this;
    invalidateReadCache(this);
    if (!silent) this.state.onChangeset?.([[value.id, this.state.objects[value.id].kind, op]]);
  }
  remove(index: number, silent?: true): void {
    let toRemove = this.value[index];
    const v = (this.state.objects[toRemove.id] as PrimitiveState).locations;
    const op: Move = {
      op: 'move',
      id: Math.random().toString(),
      loc: undefined,
      prev_gen: Math.max(0, ...v.map(p => p.prev_gen + 1)),
      author: this.state.currentAuthor
    };
    v.splice(0, v.length, op);
    toRemove.parent = undefined;
    this.value.splice(index, 1)
    invalidateReadCache(this);
    dropReadCacheDeep(toRemove);
    if (!silent) this.state.onChangeset?.([[toRemove.id, this.state.objects[toRemove.id].kind, op]]);
  }
  /**
   * @param useCache - Pass `false` to get back a fresh, unfrozen, exclusively-owned copy
   * instead of the shared memoized one — for the "read a plain-JS scratch copy, mutate it,
   * `mergeFromRawValue` it back" idiom (e.g. in `kind_service`). Skips reading *and* writing
   * `_cachedRead` at every level of the subtree (not just this node), so it's a single
   * materialization pass rather than `read()` (cached) followed by `structuredClone`.
   * @returns The materialized value of this array. **Shared, not a copy** when `useCache`
   * is true (the default) — the common (no-args) result is memoized in `_cachedRead` and
   * the same reference is returned on every call until this array's membership changes
   * (which is what makes subtree identity stable for React memoization). Callers MUST
   * treat it as read-only in that case: the result is frozen, so mutating it throws
   * rather than silently corrupting every subsequent `read()`.
   * Pass `useCache: false` if you need a copy you can mutate. The `includeKeys` path is
   * never memoized and always returns a fresh array.
   */
  read(includeKeys?: boolean, useCache: boolean = true): any {
    // The includeKeys path is only used for rare inline-editing/copy-editor reads and
    // recurses with a different shape, so we don't cache it — just the common case.
    if (includeKeys) {
      return this.value.map(v => v.read(includeKeys));
    }
    if (!useCache) {
      return this.value.map(v => v.read(undefined, false));
    }
    if (!this._readDirty && this._cachedRead !== undefined) {
      return this._cachedRead;
    }
    this._cachedRead = freezeReadResult(this.value.map(v => v.read()));
    this._readDirty = false;
    return this._cachedRead;
  }
}

export class TrackedPrimitive {
  id: string
  value: any
  state: State
  parent?: TrackedObject
  sortKey?: SortKey
  newlyAdded?: boolean
  /** Unused by TrackedPrimitive itself (its read() is O(1) already) — present so the
   * TrackedObject union has a uniform shape for `invalidateReadCache` and bulk-marking code. */
  _cachedRead?: any
  _readDirty?: boolean
  constructor (state: State, id?: string, value?: any) {
    this.id = id || 'id' + Math.random();
    this.state = state

    if (!state.objects[this.id]) {
      state.objects[this.id] = {
        kind: 'primitive',
        locations: [],
        versions: []
      }
    }

    if (id && value !== undefined) {
      this.value = value;
    } else {
      state.objects[this.id] = {
        kind: 'primitive',
        locations: [],
        versions: []
      }
      this.value = undefined
      if (value !== undefined) {
        this.set(value);
      }
    }
    state.workingSet[this.id] = this;
  }
  locateUserHere(): void {
    // update myActivity so other distro users can see what object I just added.
    if (this.state.currentAuthor?.uid) {
      this.state.myActivity = generateUserLocation(this.state.currentAuthor, this.id);
    }
  }
  set(value: any, silent?: true): void {
    const v = (this.state.objects[this.id] as PrimitiveState).versions;
    const op: Patch = {
      op: 'patch',
      id: Math.random().toString(),
      value: value,
      prev_gen: Math.max(0, ...v.map(p => p.prev_gen + 1)),
      author: this.state.currentAuthor

    };
    v.splice(0, v.length, op);
    this.value = value
    invalidateReadCache(this);
    if (!silent) this.state.onChangeset?.([[this.id, this.state.objects[this.id].kind, op]]);
  }
  read(includeIds?: boolean, _useCache: boolean = true): any {
    // Primitives don't cache (this is already O(1)) — the `_useCache` param exists only so
    // the signature matches TrackedArray/TrackedRecord for uniform recursive calls.
    // As of 8/12/2024 we only care about object IDs if this is the TrackedPrimitive of the string of a content object,
    // because that's what is inline editable.
    // This catches all Text and RichText
    const possiblyRichText = this.parent?.value ?? {};
    // This catches all plain strings inside a content/label property
    const possiblyContentOrLabel = this.parent?.parent?.value ?? {};
    if (includeIds && ('en' in possiblyRichText || 'content' in possiblyContentOrLabel || 'label' in possiblyContentOrLabel)) {
      const v = (this.state.objects[this.id] as PrimitiveState).versions;
      return {
        // This information is necessary to create operations for this TrackedObject
        // outside of the normal Distro context (e.g. for inline editing)
        _trackedObjMeta: {
          id: this.id,
          prev_gen: Math.max(0, ...v.map(p => p.prev_gen + 1)),
          currentAuthor: this.state.currentAuthor,
          userActivity: this.state.objectToActivity?.[this.id]
        },
        value: this.value
      };
    }
    return this.value;
  }
}

export class TrackedRecord {
  id: string
  value: Record<string, any>
  state: State
  parent?: TrackedObject
  sortKey?: SortKey
  newlyAdded?: boolean
  /** Cache of the last `read()` result. Invalidated (via `_readDirty`) whenever this record's properties change. */
  _cachedRead?: Record<string, any>
  _readDirty: boolean = true
  constructor (state: State, id?: string, value?: Record<string, TrackedObject>) {
    this.id = id || 'id' + Math.random();
    this.state = state
    this.value = value || {}
    if (value) {
      Object.keys(value).map(k => {
        if (state.objects[value[k].id].locations.length == 0) {
          this.set(k, value[k])
        } else {
          // Already-located child: skip set() (no re-emitted move), but still wire
          // parent so read()-cache invalidation (which walks child.parent upward)
          // reaches this record. (Reachable via ChangeUtils.deepCopyObject, whose
          // copied children retain non-empty locations.)
          value[k].parent = this;
        }
      });
    }
    if (!state.objects[this.id]) {
      state.objects[this.id] = {
        kind: 'record',
        locations: []
      }
    }
    state.workingSet[this.id] = this;
  }
  set(key: string, value: TrackedArray | TrackedPrimitive | TrackedRecord, silent?: true): void {
    // TODO: Remove from somewhere else

    const previous = this.value[key];
    const v = (this.state.objects[value.id] as PrimitiveState).locations;
    const op: Move = {
      op: 'move',
      id: Math.random().toString(),
      loc: {
        kind: 'object',
        id: this.id,
        prop: key
      },
      prev_gen: Math.max(0, ...v.map(p => p.prev_gen + 1)),
      author: this.state.currentAuthor
    };
    v.splice(0, v.length, op);
    this.value[key] = value;
    value.parent = this;
    if (previous && previous !== value) {
      previous.parent = undefined;
      dropReadCacheDeep(previous);
    }
    invalidateReadCache(this);
    if (!silent) this.state.onChangeset?.([[value.id, this.state.objects[value.id].kind, op]]);
  }
  remove(key: string, silent?: true): void {
    if (!this.value[key]?.id) return;
    const id = this.value[key].id;
    const v = (this.state.objects[this.value[key].id] as PrimitiveState).locations;
    const op: Move = {
      op: 'move',
      id: Math.random().toString(),
      loc: undefined,
      prev_gen: Math.max(0, ...v.map(p => p.prev_gen + 1)),
      author: this.state.currentAuthor
    }
    v.splice(0, v.length, op);
    const removed = this.value[key];
    removed.parent = undefined;
    delete this.value[key];
    invalidateReadCache(this);
    dropReadCacheDeep(removed);
    if (!silent) this.state.onChangeset?.([[id, this.state.objects[id].kind, op]]);
  }
  /**
   * @param useCache - Pass `false` to get back a fresh, unfrozen, exclusively-owned copy
   * instead of the shared memoized one — for the "read a plain-JS scratch copy, mutate it,
   * `mergeFromRawValue` it back" idiom (e.g. in `kind_service`). Skips reading *and* writing
   * `_cachedRead` at every level of the subtree (not just this node), so it's a single
   * materialization pass rather than `read()` (cached) followed by `structuredClone`.
   * @returns The materialized value of this record. **Shared, not a copy** when `useCache`
   * is true (the default) — the common (no-args) result is memoized in `_cachedRead` and
   * the same reference is returned on every call until this record's properties change
   * (which is what makes subtree identity stable for React memoization). Callers MUST
   * treat it as read-only in that case: the result is frozen, so mutating it throws
   * rather than silently corrupting every subsequent `read()`.
   * Pass `useCache: false` if you need a copy you can mutate. The `includeIds` path is
   * never memoized and always returns a fresh object.
   */
  read(includeIds?: boolean, useCache: boolean = true): Record<string, any> {
    // The includeIds path is only used for rare inline-editing/copy-editor reads and
    // recurses with a different shape, so we don't cache it — just the common case.
    if (useCache && !includeIds && !this._readDirty && this._cachedRead !== undefined) {
      return this._cachedRead;
    }
    const result = Object.keys(this.value).reduce((acc: Record<string, any>, k) => {
      const readValue = this.value[k].read(includeIds, useCache);
      if (typeof readValue === 'object' && '_trackedObjMeta' in readValue) {
        acc['_trackedObjInfo'] ||= {};
        acc['_trackedObjInfo'][k] = readValue['_trackedObjMeta'];
        acc[k] = readValue.value;
      } else {
        acc[k] = readValue;
      }
      return acc;
    }, {});
    if (useCache && !includeIds) {
      this._cachedRead = freezeReadResult(result);
      this._readDirty = false;
    }
    return result;
  }
  locateUserHere(): void {
    // update myActivity so other distro users can see what object I just added.
    if (this.state.currentAuthor?.uid) {
      this.state.myActivity = generateUserLocation(this.state.currentAuthor, this.id);
    }
  }
}

/**
 * Recursively converts a plain JavaScript value into a CRDT TrackedObject tree.
 * 
 * This function takes any JavaScript value (primitives, objects, arrays, or nested structures)
 * and wraps it in the appropriate TrackedObject types, making it collaborative-editable.
 * All operations are grouped into a single changeset transaction.
 * 
 * **Type mapping**:
 * - Arrays → TrackedArray (with RGA-style fractional indexing)
 * - Objects → TrackedRecord (with LWW semantics for properties)
 * - Primitives (string/number/boolean/null/undefined) → TrackedPrimitive (LWW-Register)
 * 
 * **Recursive behavior**: Nested structures are recursively converted, creating a complete
 * tree of TrackedObjects with proper parent-child relationships.
 * 
 * @param state - The CRDT state to attach the new tracked objects to
 * @param value - The plain JavaScript value to convert (any type)
 * @param isRoot - If true, creates the object with ID 'root' (only for top-level object)
 * @param needsNewId - If true, replaces any 'id' property with a new UUID (useful for copying objects)
 * @returns A TrackedObject (TrackedArray, TrackedRecord, or TrackedPrimitive) wrapping the value
 * 
 * @example
 * ```typescript
 * const state = createInitialState();
 * 
 * // Create a tracked record from plain object
 * const obj = createFromRawValue(state, { 
 *   name: 'Alice', 
 *   age: 30,
 *   tags: ['admin', 'user']
 * }, true);
 * 
 * // Now it's collaborative-editable
 * (obj as TrackedRecord).value.name.set('Bob');
 * ```
 */
export function createFromRawValue(state: State, value: any, isRoot?: true, needsNewId?: true): TrackedObject {
  openChangeset(state);
  try {
    if (Array.isArray(value)) {
      return new TrackedArray(state, isRoot ? 'root' : undefined, value.map(v => createFromRawValue(state, v, undefined, needsNewId || undefined)))
    }
    if (typeof value === 'object' && value !== null) {
      return new TrackedRecord(state, isRoot ? 'root' : undefined, Object.keys(value).reduce((acc, k) => {
        acc[k] = createFromRawValue(state, needsNewId && k === 'id' ? v4() : value[k], undefined, needsNewId || undefined);
        return acc;
      }, {} as Record<string, TrackedObject>))
    }
    return new TrackedPrimitive(state, isRoot ? 'root' : undefined, value)
  } finally {
    closeChangeset(state);
  }
}
